import { useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { useAuthStore } from "./store/authStore";
import { ThemeProvider } from "./context/ThemeContext";

// ───────────────────────────────────────────────────────────────
// PAGES — split into two buckets:
//   1. Auth/landing pages: loaded eagerly so /login, /register, /verify-email,
//      /accept-invite are instant and carry zero BP-data weight.
//   2. Heavy authenticated pages: code-split with React.lazy so the huge
//      ~200-file BP_DATA / HIERARCHY imports only evaluate when the user
//      actually navigates to them. This single change fixes the
//      "Page Unresponsive" main-thread freeze on older machines.
// ───────────────────────────────────────────────────────────────
import AuthPage from "./pages/AuthPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import UnsubscribePage from "./pages/UnsubscribePage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import HomePage from "./pages/HomePage";
import Layout from "./components/Layout";

const IAMPage                = lazy(() => import("./pages/IAMPage"));
const LibraryPage            = lazy(() => import("./pages/LibraryPage"));
const CreatePage             = lazy(() => import("./pages/CreatePage"));
const ReengineerPage         = lazy(() => import("./pages/ReengineerPage"));
const PricingPage            = lazy(() => import("./pages/PricingPage"));
const CheckoutPage           = lazy(() => import("./pages/CheckoutPage"));
const BillingPage            = lazy(() => import("./pages/BillingPage"));
const SettingsPage           = lazy(() => import("./pages/SettingsPage"));
const GuidePage              = lazy(() => import("./pages/GuidePage"));
const IndustryOnboardingPage = lazy(() => import("./pages/IndustryOnboardingPage"));
const FolderStructuresPage   = lazy(() => import("./pages/FolderStructuresPage"));
const ViewerPage             = lazy(() => import("./pages/ViewerPage"));
const UpstreamLOMPage        = lazy(() => import("./pages/UpstreamLOMPage"));
const ProcessDeepDivePage    = lazy(() => import("./pages/ProcessDeepDivePage"));
const ProcessNetworkPage     = lazy(() => import("./pages/ProcessNetworkPage"));
const VerifyInvoicePage      = lazy(() => import("./pages/VerifyInvoicePage"));
const AdminPortalRoutes      = lazy(() => import("./pages/AdminPortal"));
const SearchResultsPage      = lazy(() => import("./pages/SearchResultsPage"));
const ComparePage            = lazy(() => import("./pages/ComparePage"));
const LngLomPage             = lazy(() => import("./pages/LngLomPage").then(m => ({ default: m.LngMasterIndex })));
const LngDomainOverview      = lazy(() => import("./pages/LngLomPage").then(m => ({ default: m.LngDomainOverview })));
const LngBPDetail            = lazy(() => import("./pages/LngLomPage").then(m => ({ default: m.LngBPDetail })));
const LngActors              = lazy(() => import("./pages/LngLomPage").then(m => ({ default: m.LngActors })));
const DsMasterIndex          = lazy(() => import("./pages/DownstreamLomPage").then(m => ({ default: m.DsMasterIndex })));
const DsDomainOverview       = lazy(() => import("./pages/DownstreamLomPage").then(m => ({ default: m.DsDomainOverview })));
const DsBPDetail             = lazy(() => import("./pages/DownstreamLomPage").then(m => ({ default: m.DsBPDetail })));
const DsActors               = lazy(() => import("./pages/DownstreamLomPage").then(m => ({ default: m.DsActors })));

const RouteFallback = () => (
  <div className="loading-screen">
    <div className="spinner" />
    <p>Loading…</p>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Loading Luminex...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    const here = `${window.location.pathname}${window.location.search}`;
    const redirect = encodeURIComponent(here);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }

  return children;
};

// Admin-only route
const AdminRoute = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Loading Luminex...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== "admin") {
    return <Navigate to="/library" replace />;
  }

  return children;
};

// Superadmin-only route — hard block on /billing and /pricing for regular
// tenant users.  Silently redirects to /library so a direct URL doesn't leak
// internal-only surfaces.
const SuperadminRoute = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Loading Luminex...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user?.isSuperAdmin) {
    return <Navigate to="/library" replace />;
  }

  return children;
};

function AppContent() {
  const { init, isLoading } = useAuthStore();

  useEffect(() => {
    init();
  }, [init]);

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Initializing Luminex...</p>
      </div>
    );
  }

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'var(--bg-1)',
            color: 'var(--text-1)',
            border: '1px solid var(--border-1)',
            borderRadius: '8px',
            fontSize: '13px',
          },
          success: {
            iconTheme: {
              primary: 'var(--success)',
              secondary: 'var(--bg-1)',
            },
          },
          error: {
            iconTheme: {
              primary: 'var(--danger)',
              secondary: 'var(--bg-1)',
            },
          },
        }}
      />
      <BrowserRouter>
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/login" element={<AuthPage mode="login" />} />
          <Route path="/register" element={<AuthPage mode="register" />} />
          <Route path="/signup" element={<Navigate to="/register" replace />} />
          <Route path="/sign-up" element={<Navigate to="/register" replace />} />
          <Route path="/sign-in" element={<Navigate to="/login" replace />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          <Route path="/verify-invoice" element={<VerifyInvoicePage />} />
          <Route path="/admin-portal/*" element={<AdminPortalRoutes />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/view/:processId" element={<ViewerPage />} />
          <Route path="/home" element={<HomePage />} />
          <Route path="/" element={<HomePage />} />
          {/* Pricing is superadmin-only — hard block on direct URL access. */}
          <Route path="/pricing" element={<SuperadminRoute><PricingPage /></SuperadminRoute>} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/onboarding/industry" element={<IndustryOnboardingPage />} />
          <Route path="/lng" element={<ProtectedRoute><LngLomPage /></ProtectedRoute>} />
          <Route path="/lng/domain/:domainId" element={<ProtectedRoute><LngDomainOverview /></ProtectedRoute>} />
          <Route path="/lng/bp/:bpId" element={<ProtectedRoute><LngBPDetail /></ProtectedRoute>} />
          <Route path="/lng/actors" element={<ProtectedRoute><LngActors /></ProtectedRoute>} />
          <Route path="/downstream-lom" element={<ProtectedRoute><DsMasterIndex /></ProtectedRoute>} />
          <Route path="/downstream-lom/domain/:domainId" element={<ProtectedRoute><DsDomainOverview /></ProtectedRoute>} />
          <Route path="/downstream-lom/bp/:bpId" element={<ProtectedRoute><DsBPDetail /></ProtectedRoute>} />
          <Route path="/downstream-lom/actors" element={<ProtectedRoute><DsActors /></ProtectedRoute>} />

          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/library" replace />} />
          </Route>

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route path="library" element={<LibraryPage />} />
            <Route path="search" element={<SearchResultsPage />} />
            <Route path="compare" element={<ComparePage />} />
            <Route path="library/upstream" element={<UpstreamLOMPage />} />
            <Route path="library/process-deep-dive" element={<ProcessDeepDivePage />} />
            <Route path="library/process-network" element={<ProcessNetworkPage />} />
            <Route path="create" element={<CreatePage />} />
            <Route path="folders" element={<FolderStructuresPage />} />
            <Route path="reengineer" element={<ReengineerPage />} />
            <Route path="manage" element={<Navigate to="/folders" replace />} />
            <Route path="billing" element={<SuperadminRoute><BillingPage /></SuperadminRoute>} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="guide" element={<GuidePage />} />
            <Route path="iam" element={<AdminRoute><IAMPage /></AdminRoute>} />
          </Route>
        </Routes>
        </Suspense>
      </BrowserRouter>
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <AppContent />
      </div>
    </ThemeProvider>
  );
}

export default App;
